import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmt, fmtDate, effPrice, lineTotal, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash2, Save, FileSpreadsheet, FileText, History, Search, Pencil, SlidersHorizontal, RotateCcw, FolderPlus, GripVertical, ChevronRight, AlertTriangle, Check,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ProductLabelPanel } from "@/components/ProductLabelPanel";
import { ScaleWeightDialog } from "@/components/ScaleWeightDialog";
import { druhShort, druhBadgeClass, druhLabel } from "@/lib/druh";

function DecimalInput({ value, onValueChange, ...props }) {
  const toStr = (v) => (v === null || v === undefined || v === "" ? "" : String(v).replace(".", ","));
  const [str, setStr] = useState(() => toStr(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setStr(toStr(value)); }, [value]);
  const handle = (e) => {
    let v = e.target.value.replace(/[^0-9.,]/g, "");
    const firstComma = v.indexOf(",");
    if (firstComma !== -1) {
      v = v.slice(0, firstComma + 1) + v.slice(firstComma + 1).replace(/[.,]/g, "");
    }
    setStr(v);
    if (v === "" || v === "," || v === ".") { onValueChange(0); return; }
    const num = parseFloat(v.replace(",", "."));
    onValueChange(isNaN(num) ? 0 : num);
  };
  return <Input type="text" inputMode="decimal" {...props} value={str}
    onFocus={() => { focused.current = true; }}
    onBlur={() => { focused.current = false; setStr(toStr(value)); }}
    onChange={handle} />;
}

export default function CalculationEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [calc, setCalc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSection, setPickerSection] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [meta, setMeta] = useState({});
  const [labelKey, setLabelKey] = useState(0);
  const [scaleOpen, setScaleOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/calculations/${id}`).then((r) => {
      setCalc(r.data);
      setMeta({
        name: r.data.name, product_code: r.data.product_code || "",
        author: r.data.author || "",
        weight_g: r.data.weight_g ?? null,
        portions: r.data.portions ?? 1,
        margin_material_pct: r.data.margin_material_pct ?? 0,
        burn_pct: r.data.burn_pct ?? 1.9,
        hourly_rate: r.data.hourly_rate ?? 0,
        labor_direct_min: r.data.labor_direct_min ?? 0,
        labor_direct_margin_pct: r.data.labor_direct_margin_pct ?? 0,
        labor_indirect_min: r.data.labor_indirect_min ?? 0,
        labor_indirect_margin_pct: r.data.labor_indirect_margin_pct ?? 0,
        transport_kc: r.data.transport_kc ?? 0,
        overhead_kc: r.data.overhead_kc ?? 0,
        unassigned_label: r.data.unassigned_label || "Bez sekce",
      });
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!calc) return null;

  const sections = calc.sections || [];
  const subtotal = calc.items.reduce((a, it) => a + lineTotal(it), 0);
  const mSur = Number(meta.margin_material_pct) || 0;
  const burnPct = Number(meta.burn_pct) || 0;
  const rate = Number(meta.hourly_rate) || 0;
  const dMin = Number(meta.labor_direct_min) || 0;
  const iMin = Number(meta.labor_indirect_min) || 0;
  const dMpct = Number(meta.labor_direct_margin_pct) || 0;
  const iMpct = Number(meta.labor_indirect_margin_pct) || 0;
  const transportKc = Number(meta.transport_kc) || 0;
  const overheadKc = Number(meta.overhead_kc) || 0;
  const addMaterial = subtotal * mSur / 100;
  const addBurn = subtotal * burnPct / 100;
  const dAmt = (dMin / 60 * rate) * (1 + dMpct / 100);
  const iAmt = (iMin / 60 * rate) * (1 + iMpct / 100);
  const labor = dAmt + iAmt;
  const total = subtotal + addMaterial + addBurn + labor + transportKc + overheadKc;
  const sectionIds = new Set(sections.map((s) => s.id));
  const itemsIn = (sid) => calc.items.filter((it) => it.section_id === sid);
  const unassigned = calc.items.filter((it) => !sectionIds.has(it.section_id));
  const unassignedLabel = meta.unassigned_label || "Bez sekce";
  const sectionSubtotal = (items) => items.reduce((a, it) => a + lineTotal(it), 0);

  const updateItem = (itemId, patch) => {
    setCalc((c) => ({ ...c, items: c.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }));
    setDirty(true);
  };
  const removeItem = (itemId) => {
    setCalc((c) => ({ ...c, items: c.items.filter((it) => it.id !== itemId) }));
    setDirty(true);
  };
  const moveItem = (itemId, sectionId) => {
    setCalc((c) => ({ ...c, items: c.items.map((it) => (it.id === itemId ? { ...it, section_id: sectionId } : it)) }));
    setDirty(true);
  };
  const renameUnassigned = (name) => { setMeta((m) => ({ ...m, unassigned_label: name })); setDirty(true); };
  const addMaterials = (mats, sectionId = null) => {
    const existing = new Set(calc.items.map((i) => i.material_code));
    const newItems = mats.filter((m) => !existing.has(m.code)).map((m) => ({
      id: crypto.randomUUID(),
      material_code: m.code, material_name: m.name, druh: m.druh, category: m.category,
      unit: m.unit, quantity: 1, computed_price: m.computed_price || 0,
      global_price: m.computed_price || 0,
      override_enabled: false, override_price: null, supplier: null,
      section_id: sectionId,
    }));
    setCalc((c) => ({ ...c, items: [...c.items, ...newItems] }));
    setDirty(true);
    if (newItems.length) toast.success(`Přidáno ${newItems.length} surovin`);
  };
  const openPicker = (sectionId = null) => { setPickerSection(sectionId); setPickerOpen(true); };

  const addSection = () => {
    const s = { id: crypto.randomUUID(), name: `Sekce ${sections.length + 1}` };
    setCalc((c) => ({ ...c, sections: [...(c.sections || []), s] }));
    setDirty(true);
  };
  const renameSection = (sid, name) => {
    setCalc((c) => ({ ...c, sections: c.sections.map((s) => (s.id === sid ? { ...s, name } : s)) }));
    setDirty(true);
  };
  const removeSection = (sid) => {
    setCalc((c) => ({
      ...c,
      sections: c.sections.filter((s) => s.id !== sid),
      items: c.items.map((it) => (it.section_id === sid ? { ...it, section_id: null } : it)),
    }));
    setDirty(true);
  };

  const save = async () => {
    try {
      const r = await api.put(`/calculations/${id}`, {
        name: meta.name, product_code: meta.product_code, author: meta.author,
        weight_g: meta.weight_g === "" || meta.weight_g == null ? null : Number(meta.weight_g),
        portions: Number(meta.portions) || 1,
        margin_material_pct: Number(meta.margin_material_pct) || 0,
        burn_pct: Number(meta.burn_pct) || 0,
        hourly_rate: Number(meta.hourly_rate) || 0,
        labor_direct_min: Number(meta.labor_direct_min) || 0,
        labor_direct_margin_pct: Number(meta.labor_direct_margin_pct) || 0,
        labor_indirect_min: Number(meta.labor_indirect_min) || 0,
        labor_indirect_margin_pct: Number(meta.labor_indirect_margin_pct) || 0,
        transport_kc: Number(meta.transport_kc) || 0,
        overhead_kc: Number(meta.overhead_kc) || 0,
        unassigned_label: meta.unassigned_label || "Bez sekce",
        items: calc.items,
        sections: calc.sections || [],
      });
      setCalc(r.data);
      setDirty(false);
      setLabelKey((k) => k + 1);
      toast.success("Kalkulace uložena");
    } catch {
      toast.error("Uložení se nezdařilo");
    }
  };

  const exportFile = (kind) => {
    window.open(fileUrl(`/calculations/${id}/export/${kind}`), "_blank");
  };

  return (
    <div className="fade-up pb-24">
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="px-10 py-5">
          <button onClick={() => nav("/kalkulace")} data-testid="calc-back"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3">
            <ArrowLeft className="h-4 w-4" /> Zpět na kalkulace
          </button>
          <div className="flex items-end justify-between gap-6">
            <div className="flex-1">
              <input data-testid="calc-edit-name" value={meta.name}
                onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }}
                className="font-display text-3xl font-medium tracking-tight bg-transparent outline-none w-full border-b border-transparent focus:border-border" />
              <div className="text-xs text-muted-foreground mt-2 flex gap-4">
                <span>Vytvořeno {fmtDate(calc.created_at)}</span>
                <span>Změna {fmtDate(calc.updated_at)}</span>
              </div>
              {(calc.customer_name || calc.recipe_name) && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {calc.customer_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/10 text-secondary" data-testid="calc-header-customer">Odběratel: {calc.customer_name}</span>}
                  {calc.recipe_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">Receptura: {calc.recipe_name}</span>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setHistOpen(true)} data-testid="calc-history-btn">
                <History className="h-4 w-4 mr-2" /> Historie
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportFile("excel")} data-testid="calc-export-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="calc-export-pdf">
                <FileText className="h-4 w-4 mr-2" /> PDF
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty} className="rounded-full" data-testid="calc-save">
                <Save className="h-4 w-4 mr-2" /> Uložit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-10 py-8 space-y-6">
        {calc.price_warnings?.length > 0 && (
          <div data-testid="calc-price-warnings" className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">Upozornění: {calc.price_warnings.length} položek s nulovou cenou</div>
              <div className="text-sm text-amber-800 mt-0.5">Cena kalkulace nemusí být reálná. Doplňte nákupní ceny surovin nebo receptury polotovarů, poté vytvořte kalkulaci znovu.</div>
            </div>
            <Button variant="outline" size="sm" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={() => setWarnOpen(true)} data-testid="calc-warnings-show">Zobrazit chyby</Button>
          </div>
        )}
        <Dialog open={warnOpen} onOpenChange={setWarnOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Chyby ceny ({calc.price_warnings?.length || 0})</DialogTitle></DialogHeader>
            <div className="max-h-[60vh] overflow-auto space-y-2 pr-1">
              {(calc.price_warnings || []).map((w, i) => (
                <div key={i} className="border border-border rounded-lg px-3 py-2" data-testid={`calc-warning-${i}`}>
                  <div className="text-sm font-medium">{w.material_name} <span className="text-muted-foreground num text-xs">· {w.material_code}</span></div>
                  <div className="text-xs text-amber-700 font-medium">{w.reason}</div>
                  {w.path && <div className="text-[11px] text-muted-foreground mt-0.5">{w.path}</div>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Kód výrobku</Label>
            <Input data-testid="calc-edit-code" value={meta.product_code} className="mt-2"
              onChange={(e) => { setMeta({ ...meta, product_code: e.target.value }); setDirty(true); }} />
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Vytvořil</Label>
            <Input data-testid="calc-edit-author" value={meta.author} className="mt-2"
              onChange={(e) => { setMeta({ ...meta, author: e.target.value }); setDirty(true); }} />
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Porce</Label>
              <Button variant="outline" size="sm" className="rounded-full shrink-0 h-7" data-testid="calc-scale-btn"
                onClick={() => setScaleOpen(true)}>Přepočítat</Button>
            </div>
            <div className="flex items-end gap-2 mt-2">
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground">Gramáž porce (g)</span>
                <DecimalInput data-testid="calc-edit-weight" value={meta.weight_g}
                  onValueChange={(n) => { setMeta({ ...meta, weight_g: n }); setDirty(true); }} />
              </div>
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground">Počet porcí</span>
                <DecimalInput data-testid="calc-edit-portions" value={meta.portions}
                  onValueChange={(n) => { setMeta({ ...meta, portions: n }); setDirty(true); }} />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2" data-testid="calc-total-weight">
              Celková hmotnost: <span className="num font-medium text-foreground">{fmt((Number(meta.weight_g) || 0) * (Number(meta.portions) || 0), 0)} g</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-medium">Suroviny a obaly ({calc.items.length})</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Rozdělte položky do sekcí (pevná část, omáčka, maso, příloha…). Každá sekce se počítá zvlášť.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={addSection} data-testid="calc-add-section" className="rounded-full">
              <FolderPlus className="h-4 w-4 mr-2" /> Přidat sekci
            </Button>
            <Button size="sm" onClick={() => openPicker(null)} data-testid="calc-add-material-btn" className="rounded-full">
              <Plus className="h-4 w-4 mr-2" /> Přidat surovinu
            </Button>
          </div>
        </div>

        {calc.items.length === 0 && sections.length === 0 && (
          <div className="bg-white border border-border rounded-xl px-6 py-12 text-center text-muted-foreground text-sm">
            Zatím žádné suroviny. Přidejte sekci nebo surovinu tlačítky výše.
          </div>
        )}

        {sections.map((sec) => (
          <SectionBlock key={sec.id} section={sec} items={itemsIn(sec.id)} calc={calc}
            subtotal={sectionSubtotal(itemsIn(sec.id))} showSubtotal
            onRename={renameSection} onRemove={removeSection} onAdd={() => openPicker(sec.id)}
            onDropItem={moveItem}
            updateItem={updateItem} removeItem={removeItem} />
        ))}

        {unassigned.length > 0 && sections.length > 0 && (
          <SectionBlock section={null} items={unassigned} calc={calc}
            subtotal={sectionSubtotal(unassigned)} showSubtotal
            onAdd={() => openPicker(null)} updateItem={updateItem} removeItem={removeItem}
            onDropItem={moveItem} unassignedLabel={unassignedLabel} onRenameUnassigned={renameUnassigned} />
        )}

        {sections.length === 0 && calc.items.length > 0 && (
          <SectionBlock section={null} items={unassigned} calc={calc}
            subtotal={sectionSubtotal(unassigned)} showSubtotal={false}
            onAdd={() => openPicker(null)} updateItem={updateItem} removeItem={removeItem}
            onDropItem={moveItem} unassignedLabel={unassignedLabel} onRenameUnassigned={renameUnassigned} />
        )}

        <div className="flex justify-end">
          <div className="bg-white border border-border rounded-xl p-6 w-full max-w-md space-y-3">
            {sections.length > 0 && (
              <div className="space-y-1.5 pb-2 border-b border-border">
                {sections.map((sec) => (
                  <div key={sec.id} className="flex justify-between text-sm" data-testid={`calc-section-subtotal-${sec.id}`}>
                    <span className="text-muted-foreground truncate pr-2">{sec.name}</span>
                    <span className="num">{fmt(sectionSubtotal(itemsIn(sec.id)))} Kč</span>
                  </div>
                ))}
                {unassigned.length > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{unassignedLabel}</span>
                    <span className="num">{fmt(sectionSubtotal(unassigned))} Kč</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Cena surovin (napočítané)</span>
              <span className="num font-medium" data-testid="calc-subtotal">{fmt(subtotal)} Kč</span>
            </div>

            <div className="flex justify-between items-center gap-2 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                Marže surovinová
                <span className="inline-flex items-center gap-1">
                  <DecimalInput data-testid="calc-margin-material" value={meta.margin_material_pct}
                    onValueChange={(n) => { setMeta({ ...meta, margin_material_pct: n }); setDirty(true); }}
                    className="h-7 w-16 text-right num" />
                  <span className="text-muted-foreground">%</span>
                </span>
              </span>
              <span className="num text-primary" data-testid="calc-add-material">+ {fmt(addMaterial)} Kč</span>
            </div>

            <div className="flex justify-between items-center gap-2 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                Pálení
                <span className="inline-flex items-center gap-1">
                  <DecimalInput data-testid="calc-burn" value={meta.burn_pct}
                    onValueChange={(n) => { setMeta({ ...meta, burn_pct: n }); setDirty(true); }}
                    className="h-7 w-16 text-right num" />
                  <span className="text-muted-foreground">%</span>
                </span>
              </span>
              <span className="num text-primary" data-testid="calc-add-burn">+ {fmt(addBurn)} Kč</span>
            </div>

            <div className="text-sm space-y-1.5 border-t border-border pt-2">
              <div className="flex justify-between items-center gap-2">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  Náklady práce
                  <span className="inline-flex items-center gap-1">
                    <DecimalInput data-testid="calc-hourly-rate" value={meta.hourly_rate}
                      onValueChange={(n) => { setMeta({ ...meta, hourly_rate: n }); setDirty(true); }} className="h-6 w-16 text-right num" />
                    <span className="text-xs text-muted-foreground">Kč/h</span>
                  </span>
                </span>
                <span className="num text-primary" data-testid="calc-add-labor">+ {fmt(labor)} Kč</span>
              </div>
              <div className="flex items-center gap-2 pl-3 text-xs">
                <span className="text-muted-foreground w-16">přímý</span>
                <DecimalInput data-testid="calc-labor-direct-min" value={meta.labor_direct_min}
                  onValueChange={(n) => { setMeta({ ...meta, labor_direct_min: n }); setDirty(true); }} className="h-7 w-14 text-right num" />
                <span className="text-muted-foreground">min · marže</span>
                <DecimalInput data-testid="calc-labor-direct-margin" value={meta.labor_direct_margin_pct}
                  onValueChange={(n) => { setMeta({ ...meta, labor_direct_margin_pct: n }); setDirty(true); }} className="h-7 w-12 text-right num" />
                <span className="text-muted-foreground">%</span>
                <span className="num ml-auto text-primary">+ {fmt(dAmt)}</span>
              </div>
              <div className="flex items-center gap-2 pl-3 text-xs">
                <span className="text-muted-foreground w-16">nepřímý</span>
                <DecimalInput data-testid="calc-labor-indirect-min" value={meta.labor_indirect_min}
                  onValueChange={(n) => { setMeta({ ...meta, labor_indirect_min: n }); setDirty(true); }} className="h-7 w-14 text-right num" />
                <span className="text-muted-foreground">min · marže</span>
                <DecimalInput data-testid="calc-labor-indirect-margin" value={meta.labor_indirect_margin_pct}
                  onValueChange={(n) => { setMeta({ ...meta, labor_indirect_margin_pct: n }); setDirty(true); }} className="h-7 w-12 text-right num" />
                <span className="text-muted-foreground">%</span>
                <span className="num ml-auto text-primary">+ {fmt(iAmt)}</span>
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                Režie
                <span className="inline-flex items-center gap-1">
                  <DecimalInput data-testid="calc-overhead" value={meta.overhead_kc}
                    onValueChange={(n) => { setMeta({ ...meta, overhead_kc: n }); setDirty(true); }} className="h-7 w-20 text-right num" />
                  <span className="text-muted-foreground">Kč</span>
                </span>
              </span>
              <span className="num text-primary" data-testid="calc-add-overhead">+ {fmt(overheadKc)} Kč</span>
            </div>

            <div className="flex justify-between items-center gap-2 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                Cena doprava
                <span className="inline-flex items-center gap-1">
                  <DecimalInput data-testid="calc-transport" value={meta.transport_kc}
                    onValueChange={(n) => { setMeta({ ...meta, transport_kc: n }); setDirty(true); }} className="h-7 w-20 text-right num" />
                  <span className="text-muted-foreground">Kč</span>
                </span>
              </span>
              <span className="num text-primary" data-testid="calc-add-transport">+ {fmt(transportKc)} Kč</span>
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <div className="rounded-xl bg-primary/10 border border-primary/25 px-4 py-3 flex justify-between items-end" data-testid="calc-price-perks-box">
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wider text-primary/80 font-semibold">Cena za 1 ks</span>
                  <span className="text-[11px] text-muted-foreground">{fmt(Number(meta.portions) || 1, 0)} ks v dávce</span>
                </div>
                <span className="num text-3xl font-bold text-primary" data-testid="calc-price-perks">
                  {fmt(total / (Number(meta.portions) || 1))} Kč
                </span>
              </div>
              <div className="flex justify-between items-center px-1">
                <span className="text-sm text-muted-foreground">Celková cena dávky</span>
                <span className="num text-lg font-semibold" data-testid="calc-total">{fmt(total)} Kč</span>
              </div>
            </div>
          </div>
        </div>

        <ProductLabelPanel calcId={id} refreshKey={labelKey} />
      </div>

      <ScaleWeightDialog open={scaleOpen} onOpenChange={setScaleOpen}
        currentWeight={meta.weight_g} currentPortions={meta.portions} sections={sections} items={calc.items}
        onApply={(newItems, newW, newP) => { setCalc((c) => ({ ...c, items: newItems })); setMeta((mm) => ({ ...mm, weight_g: newW, portions: newP })); setDirty(true); }} />

      <MaterialPicker open={pickerOpen} onOpenChange={setPickerOpen}
        onAdd={(mats) => addMaterials(mats, pickerSection)} />
      <HistoryDialog open={histOpen} onOpenChange={setHistOpen} calcId={id} />
    </div>
  );
}

const METHOD_LABELS = {
  last: "poslední cena",
  period_avg: "průměr za období",
  total_avg: "celkový průměr",
};

function SectionBlock({ section, items, calc, subtotal, showSubtotal = true, onRename, onRemove, onAdd, updateItem, removeItem, onDropItem, unassignedLabel, onRenameUnassigned }) {
  const isSection = !!section;
  const [over, setOver] = useState(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const itemId = e.dataTransfer.getData("text/itemId");
    if (itemId && onDropItem) onDropItem(itemId, isSection ? section.id : null);
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={handleDrop}
      className={`bg-white rounded-xl overflow-hidden shadow-sm border-l-[5px] transition-all ${
        over ? "border border-primary ring-2 ring-primary/40" : "border border-border"
      } ${isSection ? "border-l-primary" : "border-l-secondary/60 border-dashed"}`}
      data-testid={isSection ? `calc-section-${section.id}` : "calc-section-none"}>
      <div className={`px-6 py-4 flex items-center justify-between gap-3 border-b border-border ${isSection ? "bg-primary/5" : "bg-secondary/5"}`}>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold shrink-0 ${
            isSection ? "bg-primary text-primary-foreground" : "bg-secondary/20 text-secondary"}`}>
            {isSection ? "§" : "—"}
          </span>
          {isSection ? (
            <Input value={section.name} data-testid={`calc-section-name-${section.id}`}
              onChange={(e) => onRename(section.id, e.target.value)}
              className="h-8 font-display font-semibold text-base max-w-xs border-transparent hover:border-border focus:border-border bg-transparent" />
          ) : (
            <Input value={unassignedLabel} data-testid="calc-section-name-none"
              onChange={(e) => onRenameUnassigned?.(e.target.value)}
              className="h-8 font-display font-semibold text-base max-w-xs border-transparent hover:border-border focus:border-border bg-transparent text-muted-foreground" />
          )}
          <Badge variant="outline" className="text-xs shrink-0">{items.length}</Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={onAdd} data-testid={isSection ? `calc-section-add-${section.id}` : "calc-section-add-none"}
            className="text-primary hover:bg-primary/10">
            <Plus className="h-4 w-4 mr-1.5" /> Surovina
          </Button>
          {isSection && (
            <button onClick={() => onRemove(section.id)} data-testid={`calc-section-remove-${section.id}`}
              title="Odebrat sekci" className="text-muted-foreground hover:text-destructive p-1.5">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/60 hover:bg-muted/60">
            <TableHead className="w-8"></TableHead>
            <TableHead>Surovina</TableHead>
            <TableHead className="w-20">Druh</TableHead>
            <TableHead className="w-16">MJ</TableHead>
            <TableHead className="w-28 text-right">Množství</TableHead>
            <TableHead className="w-40 text-right">Cena/MJ</TableHead>
            <TableHead className="w-32 text-right">Mezisoučet</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">
              {over ? "Pusťte surovinu sem" : "Žádné suroviny — přetáhněte je sem nebo přidejte tlačítkem."}
            </TableCell></TableRow>
          )}
          {items.map((it) => (
            <ItemRow key={it.id} it={it} calc={calc} updateItem={updateItem} removeItem={removeItem} />
          ))}
        </TableBody>
      </Table>
      {showSubtotal && (
        <div className={`px-6 py-3 border-t-2 flex justify-end items-center gap-6 ${isSection ? "border-t-primary/30 bg-primary/5" : "border-t-secondary/30 bg-secondary/5"}`}>
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Mezisoučet · {isSection ? section.name : unassignedLabel}
          </span>
          <span className="num font-bold" data-testid={isSection ? `calc-section-sub-${section.id}` : "calc-section-sub-none"}>
            {fmt(subtotal)} Kč
          </span>
        </div>
      )}
    </div>
  );
}

function ItemRow({ it, calc, updateItem, removeItem }) {
  const [open, setOpen] = useState(false);
  const editSubPrice = (path, value) => {
    const sc = JSON.parse(JSON.stringify(it.subcalc));
    let items = sc.items, node = null;
    for (let k = 0; k < path.length; k++) {
      node = items[path[k]];
      items = node.subcalc ? node.subcalc.items : null;
    }
    if (!node) return;
    node.manual = value !== null && value !== undefined;
    node.manual_price = node.manual ? value : null;
    recalcSub(sc);
    updateItem(it.id, { subcalc: sc, computed_price: sc.unit_price });
  };
  return (
    <>
    <TableRow data-testid={`calc-item-${it.material_code}`} draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/itemId", it.id); e.dataTransfer.effectAllowed = "move"; }}
      className="cursor-grab active:cursor-grabbing">
      <TableCell className="text-muted-foreground/50">
        <GripVertical className="h-4 w-4" data-testid={`calc-item-drag-${it.material_code}`} />
      </TableCell>
      <TableCell>
        <div className="flex items-start gap-1.5">
          {it.subcalc && (
            <button onClick={() => setOpen((o) => !o)} data-testid={`calc-subcalc-toggle-${it.material_code}`}
              title="Zobrazit podkalkulaci polotovaru"
              className="mt-0.5 text-primary hover:bg-primary/10 rounded p-0.5 shrink-0">
              <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          )}
          <div>
            <div className="font-medium">{it.material_name}</div>
            <div className="text-xs text-muted-foreground num">{it.material_code} · {it.category}{it.subcalc ? " · podkalkulace" : ""}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={druhBadgeClass(it.druh)}>
          {druhShort(it.druh)}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{it.unit}</TableCell>
      <TableCell className="text-right">
        <DecimalInput data-testid={`calc-qty-${it.material_code}`}
          value={it.quantity} className="h-8 text-right num w-24 ml-auto"
          onValueChange={(n) => updateItem(it.id, { quantity: n })} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {it.override_enabled && (
            <Badge variant="outline" className="border-secondary/50 text-secondary bg-secondary/10 text-[9px] px-1 py-0" data-testid={`calc-manual-badge-${it.material_code}`}>ručně</Badge>
          )}
          {it.override_enabled ? (
            <DecimalInput data-testid={`calc-override-price-${it.material_code}`}
              value={it.override_price ?? ""} placeholder="cena"
              className="h-8 text-right num w-24 border-secondary"
              onValueChange={(n) => updateItem(it.id, { override_price: n })} />
          ) : (
            <span className="num">{fmt(it.computed_price)}</span>
          )}
          {!it.override_enabled && (
            <LinePriceConfig item={it} calc={calc} onApply={(patch) => updateItem(it.id, patch)} />
          )}
          <button
            data-testid={`calc-override-toggle-${it.material_code}`}
            title="Přepsat cenu ručně"
            onClick={() => updateItem(it.id, {
              override_enabled: !it.override_enabled,
              override_price: !it.override_enabled ? effPrice(it) : it.override_price,
            })}
            className={`p-1.5 rounded-md transition-colors ${it.override_enabled ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        {!it.override_enabled && it.price_method && (
          <div className="mt-1 text-right" data-testid={`calc-price-custom-${it.material_code}`}>
            <div className="text-[10px] text-primary">{lineConfigLabel(it)}</div>
            {priceDiff(it) !== null && Math.abs(priceDiff(it)) >= 0.005 && (
              <div className={`text-[10px] font-semibold ${priceDiff(it) < 0 ? "text-primary" : "text-secondary"}`}
                data-testid={`calc-price-diff-${it.material_code}`}>
                {priceDiff(it) < 0 ? "▼" : "▲"} {priceDiff(it) > 0 ? "+" : ""}{fmt(priceDiff(it))} Kč vs. nastavení
              </div>
            )}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right num font-semibold">{fmt(lineTotal(it))}</TableCell>
      <TableCell>
        <button onClick={() => removeItem(it.id)} data-testid={`calc-remove-${it.material_code}`}
          className="text-muted-foreground hover:text-destructive p-1">
          <Trash2 className="h-4 w-4" />
        </button>
      </TableCell>
    </TableRow>
    {it.subcalc && open && (
      <TableRow data-testid={`calc-subcalc-${it.material_code}`}>
        <TableCell colSpan={8} className="bg-muted/30 py-3">
          <PolotovarBreakdown subcalc={it.subcalc} onEditPrice={editSubPrice} />
        </TableCell>
      </TableRow>
    )}
    </>
  );
}

function flattenSub(subcalc, depth, out, path) {
  out.push({ type: "header", depth, path, recipe_name: subcalc.recipe_name, recipe_code: subcalc.recipe_code,
    portions: subcalc.portions, subtotal: subcalc.subtotal, unit_price: subcalc.unit_price });
  subcalc.items.forEach((si, idx) => {
    const p = [...path, idx];
    out.push({ type: "item", depth, path: p, ...si });
    if (si.subcalc) flattenSub(si.subcalc, depth + 1, out, p);
  });
}

function recalcSub(sc) {
  let subtotal = 0;
  for (const it of sc.items) {
    let price;
    if (it.subcalc) { recalcSub(it.subcalc); price = it.subcalc.unit_price; }
    else { price = it.manual ? (it.manual_price || 0) : (it.price || 0); }
    it.eff_price = price;
    it.line_total = Math.round(price * (it.quantity || 0) * 100) / 100;
    subtotal += price * (it.quantity || 0);
  }
  sc.subtotal = Math.round(subtotal * 100) / 100;
  sc.unit_price = sc.portions ? Math.round((subtotal / sc.portions) * 10000) / 10000 : Math.round(subtotal * 10000) / 10000;
}

function SubLeafPrice({ row, onEditPrice }) {
  const [open, setOpen] = useState(false);
  const eff = row.eff_price ?? row.price;
  return (
    <span className="flex items-center justify-end gap-1.5 shrink-0">
      {row.manual && (
        <Badge variant="outline" className="border-secondary/50 text-secondary bg-secondary/10 text-[9px] px-1 py-0" data-testid={`sub-manual-badge-${row.material_code}`}>ručně</Badge>
      )}
      {open ? (
        <span className="flex items-center gap-1">
          <DecimalInput value={row.manual_price ?? eff} className="h-6 w-20 text-right num text-xs" autoFocus
            data-testid={`sub-price-input-${row.material_code}`}
            onValueChange={(n) => onEditPrice(row.path, n)} />
          <button onClick={() => setOpen(false)} className="text-primary p-0.5" title="Hotovo"><Check className="h-3.5 w-3.5" /></button>
        </span>
      ) : (
        <span className="flex items-center gap-1 num">
          <span className={eff === 0 ? "text-amber-600" : "text-muted-foreground"}>
            {fmt(eff)} × {fmt(row.quantity)} = <b className="text-foreground">{fmt(row.line_total)} Kč</b>
          </span>
          <button onClick={() => setOpen(true)} title="Doplnit cenu ručně" data-testid={`sub-price-edit-${row.material_code}`}
            className={`p-0.5 rounded ${eff === 0 ? "text-amber-600 hover:bg-amber-100" : "text-muted-foreground hover:text-secondary"}`}>
            <Pencil className="h-3 w-3" />
          </button>
          {row.manual && (
            <button onClick={() => onEditPrice(row.path, null)} title="Zrušit ruční cenu"
              className="text-muted-foreground hover:text-destructive p-0.5"><RotateCcw className="h-3 w-3" /></button>
          )}
        </span>
      )}
    </span>
  );
}

function PolotovarBreakdown({ subcalc, onEditPrice }) {
  const rows = [];
  flattenSub(subcalc, 0, rows, []);
  return (
    <div className="bg-muted/20 border border-border rounded-lg p-2 space-y-px">
      {rows.map((r, i) => {
        const guides = [];
        for (let d = 0; d < r.depth; d++) {
          guides.push(<span key={d} className="w-4 shrink-0 self-stretch border-l-2 border-primary/20 ml-1.5" />);
        }
        if (r.type === "header") {
          return (
            <div key={i} className="flex items-stretch">
              {guides}
              <div className="flex-1 flex flex-wrap justify-between items-center gap-x-4 gap-y-0.5 text-xs font-semibold bg-primary/10 border border-primary/30 border-l-4 border-l-primary rounded-md px-2.5 py-1.5 mt-1.5">
                <span className="text-primary">
                  Podkalkulace: {r.recipe_name} <span className="text-muted-foreground num font-normal">({r.recipe_code})</span>
                </span>
                <span className="text-muted-foreground font-normal">
                  Porcí: <span className="num">{fmt(r.portions)}</span> · Cena surovin: <span className="num">{fmt(r.subtotal)} Kč</span> · Cena/jedn.: <b className="num text-primary">{fmt(r.unit_price)} Kč</b>
                </span>
              </div>
            </div>
          );
        }
        const eff = r.eff_price ?? r.price;
        return (
          <div key={i} className="flex items-stretch">
            {guides}
            <div className="flex-1 flex justify-between items-center text-xs py-1 px-2 gap-2 bg-white/70 border-b border-border/50">
              <span className="flex items-center gap-1.5 min-w-0">
                <Badge variant="outline" className={`${druhBadgeClass(r.druh)} text-[9px] px-1 py-0 shrink-0`}>{druhShort(r.druh)}</Badge>
                <span className="truncate">{r.material_name}</span>
                <span className="text-muted-foreground num shrink-0">· {fmt(r.quantity)} {r.unit}</span>
                {eff === 0 && !r.subcalc && <span className="text-amber-600 text-[10px] shrink-0" title="Nulová cena">⚠</span>}
              </span>
              {r.subcalc ? (
                <span className="num shrink-0 text-muted-foreground">{fmt(eff)} × {fmt(r.quantity)} = <b className="text-foreground">{fmt(r.line_total)} Kč</b></span>
              ) : (
                <SubLeafPrice row={r} onEditPrice={onEditPrice} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function lineConfigLabel(it) {
  const parts = [];
  if (it.price_method) parts.push(METHOD_LABELS[it.price_method] || it.price_method);
  if (it.price_supplier) parts.push(it.price_supplier);
  if (it.price_method === "period_avg" && (it.price_period_from || it.price_period_to))
    parts.push(`${it.price_period_from || "…"}–${it.price_period_to || "…"}`);
  return "⚙ " + parts.join(" · ");
}

function priceDiff(it) {
  if (it.global_price === null || it.global_price === undefined) return null;
  if (it.computed_price === null || it.computed_price === undefined) return null;
  return it.computed_price - it.global_price;
}

function LinePriceConfig({ item, calc, onApply }) {
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [method, setMethod] = useState(item.price_method || calc.method || "last");
  const [base, setBase] = useState(item.price_base || calc.price_base || "netto");
  const [supplier, setSupplier] = useState(item.price_supplier || "__all__");
  const [pf, setPf] = useState(item.price_period_from || "");
  const [pt, setPt] = useState(item.price_period_to || "");
  const [preview, setPreview] = useState(item.computed_price);
  const [globalRef, setGlobalRef] = useState(item.global_price ?? null);

  useEffect(() => {
    if (open) {
      api.get(`/materials/${item.material_code}/history`).then((r) => setSuppliers(r.data.suppliers || []));
      if (globalRef === null || globalRef === undefined) {
        api.get(`/materials/${item.material_code}/price`).then((r) => setGlobalRef(r.data.price));
      }
    }
  }, [open, item.material_code]);

  useEffect(() => {
    if (!open) return;
    const params = { method, price_base: base };
    if (supplier !== "__all__") params.supplier_filter = supplier;
    if (method === "period_avg") { params.period_from = pf; params.period_to = pt; }
    api.get(`/materials/${item.material_code}/price`, { params }).then((r) => setPreview(r.data.price));
  }, [open, method, base, supplier, pf, pt, item.material_code]);

  const diff = (preview !== null && preview !== undefined && globalRef !== null && globalRef !== undefined)
    ? preview - globalRef : null;

  const apply = () => {
    onApply({
      computed_price: preview || 0,
      global_price: globalRef ?? item.global_price ?? null,
      price_method: method,
      price_base: base,
      price_supplier: supplier === "__all__" ? null : supplier,
      price_period_from: method === "period_avg" ? pf || null : null,
      price_period_to: method === "period_avg" ? pt || null : null,
    });
    setOpen(false);
  };

  const reset = () => {
    onApply({
      computed_price: (globalRef ?? item.global_price ?? item.computed_price) || 0,
      price_method: null, price_base: null, price_supplier: null,
      price_period_from: null, price_period_to: null,
    });
    setMethod(calc.method || "last");
    setBase(calc.price_base || "netto");
    setSupplier("__all__"); setPf(""); setPt("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`calc-priceconfig-${item.material_code}`}
          title="Nastavit výpočet ceny (dodavatel, období)"
          className={`p-1.5 rounded-md transition-colors ${item.price_method ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-left" align="end">
        <div className="space-y-3">
          <div className="font-display font-medium text-sm">Výpočet ceny této suroviny</div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Metoda</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-9 mt-1" data-testid={`priceconfig-method-${item.material_code}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="last">Poslední cena</SelectItem>
                <SelectItem value="period_avg">Průměr za období</SelectItem>
                <SelectItem value="total_avg">Celkový průměr</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cenový základ</Label>
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="netto">Netto (bez DPH)</SelectItem>
                <SelectItem value="brutto">Brutto (s DPH)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Dodavatel</Label>
            <Select value={supplier} onValueChange={setSupplier}>
              <SelectTrigger className="h-9 mt-1" data-testid={`priceconfig-supplier-${item.material_code}`}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-56">
                <SelectItem value="__all__">Všichni dodavatelé</SelectItem>
                {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {method === "period_avg" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Od</Label>
                <Input type="date" value={pf} onChange={(e) => setPf(e.target.value)} className="h-9 mt-1"
                  data-testid={`priceconfig-from-${item.material_code}`} />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Do</Label>
                <Input type="date" value={pt} onChange={(e) => setPt(e.target.value)} className="h-9 mt-1"
                  data-testid={`priceconfig-to-${item.material_code}`} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
            <span className="text-xs text-muted-foreground">Vypočtená cena</span>
            <span className="num font-bold" data-testid={`priceconfig-preview-${item.material_code}`}>
              {fmt(preview)} Kč
            </span>
          </div>

          <div className="flex items-center justify-between px-3 text-xs">
            <span className="text-muted-foreground">Dle globálního nastavení</span>
            <span className="num text-muted-foreground">{fmt(globalRef)} Kč</span>
          </div>
          {diff !== null && Math.abs(diff) >= 0.005 && (
            <div className={`flex items-center justify-between px-3 text-xs font-semibold ${diff < 0 ? "text-primary" : "text-secondary"}`}
              data-testid={`priceconfig-diff-${item.material_code}`}>
              <span>Rozdíl</span>
              <span className="num">{diff < 0 ? "▼ " : "▲ +"}{fmt(diff)} Kč</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={reset} className="text-muted-foreground"
              data-testid={`priceconfig-reset-${item.material_code}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Dle nastavení
            </Button>
            <Button size="sm" onClick={apply} data-testid={`priceconfig-apply-${item.material_code}`}>Použít</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MaterialPicker({ open, onOpenChange, onAdd }) {
  const [mats, setMats] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) { setSelected({}); setSearch(""); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/materials", { params: search ? { search } : {} }).then((r) => setMats(r.data.materials));
    }, 250);
    return () => clearTimeout(t);
  }, [search, open]);

  const toggle = (m) => setSelected((s) => ({ ...s, [m.code]: s[m.code] ? undefined : m }));
  const confirm = () => {
    const chosen = Object.values(selected).filter(Boolean);
    if (chosen.length) onAdd(chosen);
    onOpenChange(false);
  };
  const count = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Přidat suroviny z číselníku</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="picker-search" placeholder="Hledat…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" autoFocus />
        </div>
        <div className="max-h-80 overflow-auto border border-border rounded-lg divide-y divide-border">
          {mats.slice(0, 100).map((m) => (
            <button key={m.code} data-testid={`picker-item-${m.code}`} onClick={() => toggle(m)}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${selected[m.code] ? "bg-primary/10" : "hover:bg-muted"}`}>
              <div>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground num">{m.code} · {m.category} · {druhLabel(m.druh)}</div>
              </div>
              <div className="num text-sm font-semibold">{fmt(m.computed_price)} Kč/{m.unit}</div>
            </button>
          ))}
          {mats.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nic nenalezeno.</div>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušit</Button>
          <Button onClick={confirm} disabled={!count} data-testid="picker-confirm">Přidat {count > 0 ? `(${count})` : ""}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, onOpenChange, calcId }) {
  const [hist, setHist] = useState([]);
  useEffect(() => {
    if (open) api.get(`/calculations/${calcId}`).then((r) => setHist(r.data.history || []));
  }, [open, calcId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
          <History className="h-5 w-5 text-primary" /> Historie změn
        </DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-auto space-y-3">
          {[...hist].reverse().map((h, i) => (
            <div key={i} className="border-l-2 border-primary/30 pl-4 py-1" data-testid={`history-entry-${i}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{h.action}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString("cs-CZ")}</span>
              </div>
              <div className="text-sm mt-1">{h.note}</div>
              <div className="text-xs text-muted-foreground">{h.author}</div>
            </div>
          ))}
          {hist.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Žádné záznamy.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
